import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AppSeo } from "@/components/AppSeo";
import { EmailRenderCheckMatrix } from "@/components/admin/EmailRenderCheckMatrix";
import { EmailDeliveryAuditPanel } from "@/components/admin/EmailDeliveryAuditPanel";

type TemplateRow = {
  templateName: string;
  displayName: string;
  subject: string;
  html: string;
  status: "ready" | "preview_data_required" | "render_failed";
  errorMessage?: string;
  previewData?: unknown;
};

const AdminEmailPreview = () => {
  const [templates, setTemplates] = useState<TemplateRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("list-email-templates", { body: {} });
    if (error) {
      toast.error(error.message ?? "Failed to load templates");
      setTemplates([]);
      setLoading(false);
      return;
    }
    const rows = (data?.templates ?? []) as TemplateRow[];
    setTemplates(rows);
    if (rows.length && !selected) setSelected(rows[0].templateName);
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(
    () => templates?.find((t) => t.templateName === selected) ?? null,
    [templates, selected],
  );

  const openInNewTab = () => {
    if (!active?.html) return;
    const blob = new Blob([active.html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const sendTestToMe = async () => {
    if (!active || active.status !== "ready") return;
    setSending(true);
    try {
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user?.email) {
        toast.error("Could not resolve your account email.");
        return;
      }
      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: active.templateName,
          recipientEmail: userData.user.email,
          templateData: active.previewData ?? {},
        },
      });
      if (error) {
        toast.error(error.message ?? "Failed to send test email.");
        return;
      }
      toast.success(`Test email sent to ${userData.user.email}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send test email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <AppSeo title="Email preview" />
    <div className="container mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2">
            <Link to="/admin">
              <ArrowLeft className="h-4 w-4" /> Back to admin
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Email template preview</h1>
          <p className="text-sm text-muted-foreground">
            Renders every registered transactional template with its sample preview data. No emails are sent.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="text-base">Templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {loading && !templates ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)
            ) : !templates?.length ? (
              <p className="text-sm text-muted-foreground">No templates registered.</p>
            ) : (
              templates.map((t) => {
                const isActive = t.templateName === selected;
                return (
                  <button
                    key={t.templateName}
                    type="button"
                    onClick={() => setSelected(t.templateName)}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm transition ${
                      isActive
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border bg-card hover:bg-muted"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">{t.displayName}</span>
                      {t.status !== "ready" && (
                        <Badge
                          variant={t.status === "render_failed" ? "destructive" : "secondary"}
                          className="shrink-0 text-[10px]"
                        >
                          {t.status === "render_failed" ? "error" : "no preview"}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">{t.templateName}</div>
                  </button>
                );
              })
            )}
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <CardTitle className="truncate text-base">{active?.displayName ?? "Select a template"}</CardTitle>
                {active?.subject && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Subject:</span> {active.subject}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void sendTestToMe()}
                  disabled={sending || !active || active.status !== "ready"}
                  title="Send this template to your own account email"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send test to me
                </Button>
                <Button variant="outline" size="sm" onClick={openInNewTab} disabled={!active?.html}>
                  <ExternalLink className="h-4 w-4" /> Open in new tab
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!active ? (
              <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Pick a template on the left."}
              </div>
            ) : active.status === "render_failed" ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                Failed to render: {active.errorMessage ?? "unknown error"}
              </div>
            ) : active.status === "preview_data_required" ? (
              <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
                This template has no <code>previewData</code> registered, so it can't be rendered without real props.
                Add <code>previewData</code> in the template's <code>registry.ts</code> entry to preview it here.
              </div>
            ) : (
              <iframe
                title={`Preview of ${active.displayName}`}
                srcDoc={active.html}
                sandbox=""
                className="h-[75vh] w-full rounded-md border border-border bg-white"
              />
            )}
          </CardContent>
        </Card>
      </div>

      <EmailRenderCheckMatrix
        templates={templates?.map((t) => ({ templateName: t.templateName, displayName: t.displayName })) ?? null}
      />

      <EmailDeliveryAuditPanel />
    </div>
    </>
  );
};

export default AdminEmailPreview;
